import { buildingLogger } from './Logger';

export const PRESTIGE_UPGRADE_IDS = {
  CRAFTING_SPEED: 'prestige-crafting-speed',
} as const;

export type PrestigeUpgradeId =
  (typeof PRESTIGE_UPGRADE_IDS)[keyof typeof PRESTIGE_UPGRADE_IDS];

interface PrestigeUpgradeConfig {
  readonly id: PrestigeUpgradeId;
  readonly name: string;
  readonly description: string;
  readonly effectDescription: string;
  readonly cost: number;
}

export const PRESTIGE_UPGRADES: Record<
  PrestigeUpgradeId,
  PrestigeUpgradeConfig
> = {
  [PRESTIGE_UPGRADE_IDS.CRAFTING_SPEED]: {
    id: PRESTIGE_UPGRADE_IDS.CRAFTING_SPEED,
    name: 'Assembly Mastery',
    description:
      'Permanent knowledge carried between runs that accelerates all crafting.',
    effectDescription: '+50% crafting speed for assemblers and factories.',
    cost: 1,
  },
};

export interface PrestigeUpgradeState {
  readonly purchased: Partial<Record<PrestigeUpgradeId, number>>;
  readonly selectedUpgrade: PrestigeUpgradeId | null;
}

export interface SerializedPrestigeUpgradeState {
  readonly purchased?: Partial<Record<PrestigeUpgradeId, number>>;
  readonly selectedUpgrade?: PrestigeUpgradeId | null;
}

export const createInitialPrestigeUpgradeState = (): PrestigeUpgradeState => ({
  purchased: {},
  selectedUpgrade: null,
});

export const deserializePrestigeUpgradeState = (
  data?: SerializedPrestigeUpgradeState | null
): PrestigeUpgradeState => {
  if (!data) {
    return createInitialPrestigeUpgradeState();
  }

  return {
    purchased: { ...(data.purchased ?? {}) },
    selectedUpgrade: data.selectedUpgrade ?? null,
  };
};

export const serializePrestigeUpgradeState = (
  state: PrestigeUpgradeState
): SerializedPrestigeUpgradeState => ({
  purchased: { ...state.purchased },
  selectedUpgrade: state.selectedUpgrade,
});

export const isPrestigeUpgradePurchased = (
  state: PrestigeUpgradeState,
  upgradeId: PrestigeUpgradeId
): boolean => {
  return (state.purchased[upgradeId] ?? 0) > 0;
};

export const purchasePrestigeUpgradeState = (
  state: PrestigeUpgradeState,
  upgradeId: PrestigeUpgradeId
): PrestigeUpgradeState => {
  const currentCount = state.purchased[upgradeId] ?? 0;
  const nextPurchased = {
    ...state.purchased,
    [upgradeId]: currentCount + 1,
  };

  buildingLogger.info('Prestige upgrade purchased', {
    upgradeId,
    totalPurchases: nextPurchased[upgradeId],
  });

  return {
    ...state,
    purchased: nextPurchased,
  };
};

export const selectPrestigeUpgrade = (
  state: PrestigeUpgradeState,
  upgradeId: PrestigeUpgradeId | null
): PrestigeUpgradeState => {
  return {
    ...state,
    selectedUpgrade: upgradeId,
  };
};

export const getPrestigeCraftingSpeedMultiplier = (
  state: PrestigeUpgradeState
): number => {
  const purchases = state.purchased[PRESTIGE_UPGRADE_IDS.CRAFTING_SPEED] ?? 0;
  if (purchases <= 0) {
    return 1;
  }

  return 1 + 0.5 * purchases;
};
